import { memo } from 'react';

/**
 * §17 — Offside line overlay for the 2D tactics board.
 *
 * Renders a Hawk-Eye-style dashed yellow line across the pitch at the
 * z-coordinate of each team's second-to-last defender (computed in
 * `@4cm/core/tactics/overlays.computeOffsideLines`). One line per team,
 * either independently nullable when that team has fewer than two
 * outfielders on the pitch.
 *
 * Coordinate mapping mirrors VoronoiLayer2D:
 *   SVG-x = -world.z   (so a world z-line maps to a vertical svg-x)
 *   SVG-y =  world.x
 * The offside line spans the full pitch width, so its world endpoints
 * are `(x = ±FIELD_WIDTH/2, z = lineZ)`. After the swap that maps to
 * two SVG points with the same `-lineZ` x and ±halfWidth y — i.e. a
 * vertical line in SVG space, matching the broadcast convention where
 * the field's length runs left-to-right and the offside line cuts top
 * to bottom across it.
 *
 * Style: Hawk-Eye / Sky MNF — `#facc15` (Tailwind yellow-400), 0.3 m
 * stroke, broadcast-dashed `1.5 0.8`. The opacity prop bakes into rgba.
 */

/** Pitch width in metres. Matches the canonical
 *  `FIELD_WIDTH_M = 68` from `@4cm/core/tactics/adapters` — duplicated
 *  here so the component doesn't pull a runtime import from a peer
 *  package for a literal that never changes. */
const FIELD_WIDTH_M = 68;

export interface OffsideLineLayer2DProps {
  /** World z-coordinate of home's offside line, or `null` when home has
   *  fewer than 2 outfielders. */
  homeLineZ: number | null;
  /** World z-coordinate of away's offside line, or `null` when away has
   *  fewer than 2 outfielders. */
  awayLineZ: number | null;
  /** Caller-controlled opacity in [0, 1]. Bakes into the stroke's rgba
   *  so consumers don't need an extra `opacity` attribute. */
  opacity: number;
}

/** Hawk-Eye yellow with baked opacity. Same colour family on both
 *  teams — broadcasters never distinguish the home / away offside lines
 *  by colour. */
function strokeFor(opacity: number): string {
  return `rgba(250, 204, 21, ${opacity})`;
}

function OffsideLineLayer2DInner({
  homeLineZ,
  awayLineZ,
  opacity,
}: OffsideLineLayer2DProps) {
  // Nothing to draw if both teams are below the 2-outfielder threshold.
  if (homeLineZ === null && awayLineZ === null) return null;

  const halfW = FIELD_WIDTH_M / 2;
  const stroke = strokeFor(opacity);

  // World endpoints (±halfW, lineZ) → SVG (-lineZ, ±halfW). The two
  // endpoints share x and differ only in y, so the line paints
  // vertically across the pitch (top-to-bottom in landscape).
  const lineFor = (lineZ: number, key: string, team: 'home' | 'away') => (
    <line
      key={key}
      x1={-lineZ}
      y1={-halfW}
      x2={-lineZ}
      y2={halfW}
      stroke={stroke}
      strokeWidth={0.3}
      strokeDasharray="1.5 0.8"
      data-team={team}
      data-testid={`t2d-offside-line-${team}`}
    />
  );

  return (
    <g
      className="t2d-offside-layer"
      data-testid="t2d-offside-layer"
      pointerEvents="none"
    >
      {homeLineZ !== null && lineFor(homeLineZ, 'home', 'home')}
      {awayLineZ !== null && lineFor(awayLineZ, 'away', 'away')}
    </g>
  );
}

/** Memoised — line positions only change when underlying tokens move,
 *  so the parent typically hands stable z-values across HUD-only
 *  re-renders. */
export const OffsideLineLayer2D = memo(OffsideLineLayer2DInner);
